import { handleMutation } from '@/helpers/toastHelper';
import { useCallback, useMemo } from 'react';
import { getIconArrayByCategory } from '../../helpers/dropdownOptionHelper';
import { sortSimpleObjectArray } from '../../helpers/helper';
import { IconCategory, TaskType } from '../../services/tasks/task.types';
import {
  CreateTask,
  ReadTask,
  UpdateTask,
  useCreateTasksMutation,
  useDeleteTasksMutation,
  useGetTasksQuery,
  useUpdateTasksMutation
} from '../gql/graphql.generated';

export const useTasks = () => {
  const [{ data }] = useGetTasksQuery();

  const tasks = useMemo(() => {
    return data?.tasks ? (sortSimpleObjectArray(gqlToTasks(data.tasks as any), 'name') as TaskType[]) : [];
  }, [data?.tasks]);

  return tasks;
};

export const useActiveTasks = () => {
  const tasks = useTasks();

  return useMemo(() => {
    return tasks.filter(t => t.isActive);
  }, [tasks]);
};

export const useCreateTasks = () => {
  const [responseData, create] = useCreateTasksMutation();

  const createTasks = useCallback(
    async (tasks: TaskType[]) => {
      const result = await create({ tasks: tasksToGQL(tasks) });
      return result;
    },
    [create]
  );

  return { fetching: responseData.fetching, create: createTasks };
};

export const useUpdateTasks = () => {
  const [responseData, update] = useUpdateTasksMutation();

  const updateTasks = useCallback(
    async (tasks: TaskType[]) => {
      return update({ tasks: tasksToGQL(tasks, true) as UpdateTask[] });
    },
    [update]
  );

  return { fetching: responseData.fetching, update: updateTasks };
};

export const useDeleteTasks = () => {
  const [{ fetching }, deleteTasksBulk] = useDeleteTasksMutation();

  const deleteTasks = useCallback(
    async (taskIds: number[]) => {
      const success = await handleMutation(() => deleteTasksBulk({ taskIds }), {
        successMessage: 'Tasks deleted successfully.',
        errorMessage: 'Failed to delete tasks.',
        customSuccessCheck: data => data?.deleteTasks === true
      });
      return { error: success ? undefined : new Error('Failed to delete tasks') };
    },
    [deleteTasksBulk]
  );

  return { deleteTasks, fetching };
};

export const tasksToGQL = (tasks: TaskType[], isUpdate?: boolean): CreateTask[] | UpdateTask[] => {
  return tasks.map(t => {
    const taskProps = {
      displayName: t.displayName,
      icon: t.icon,
      textColor: t.textColor,
      color: t.color,
      defaultDuration: t.defaultDuration,
      isSupplementary: t.isSupplementary,
      isInMonthlyTotals: t.isInMonthlyTotals,
      isInCalculatedWorkingHours: t.isInCalculatedWorkingHours
    };

    if (!isUpdate) {
      return {
        name: t.name,
        active: t.isActive,
        sortOrder: 1,
        stationId: 1, // Should use real id
        skills: t.skills,
        optionalSkills: t.optionalSkills,
        properties: JSON.stringify(taskProps)
      };
    }

    return {
      id: parseInt(t.id),
      name: t.name,
      sortOrder: 1,
      stationId: 1, // Should use real id
      active: t.isActive,
      skills: t.skills,
      optionalSkills: t.optionalSkills,
      properties: JSON.stringify(taskProps)
    };
  });
};

export const gqlToTasks = (tasks: ReadTask[]): TaskType[] => {
  return tasks.map(t => {
    let props: any = {};
    if (t.properties) {
      props = JSON.parse(t.properties);
    }

    const iconObj =
      typeof props.icon === 'string'
        ? {
            icon: props.icon,
            category: IconCategory.standard
          }
        : props.icon;

    return {
      id: t.id.toString(),
      name: t.name,
      isActive: !!t.active,
      displayName: props.displayName,
      icon: iconObj,
      textColor: props.textColor,
      color: props.color,
      defaultDuration: parseInt(props.defaultDuration),
      isSupplementary: props.isSupplementary,
      isInMonthlyTotals: props.isInMonthlyTotals,
      isInCalculatedWorkingHours: props.isInCalculatedWorkingHours ?? true,
      skills: t.skills?.map(s => s.id) || [],
      optionalSkills: t.optionalSkills?.map(s => s.id) || []
    };
  });
};

export const useGetTask = () => {
  const taskList = useTasks();

  return useCallback(
    (taskId: string) => {
      const task = taskList.find(t => t.id === taskId);

      return task;
    },
    [taskList]
  );
};

export const useGetTaskIcon = () => {
  const taskList = useTasks();

  return useCallback(
    (taskId: string) => {
      const task = taskList.find(t => t.id === taskId);

      if (!task || !task.icon) {
        return undefined;
      }

      const iconArray = getIconArrayByCategory(task.icon.category);
      const iconOption = iconArray.find(opt => opt.id.toString() === task?.icon.icon);

      return iconOption?.value;
    },
    [taskList]
  );
};
